/**
 * HR Org-structure import — a tab in the Import & Export hub.
 *
 * One CSV (`function,department,team`) → previews and commits the whole
 * structural tree (functions → departments → teams). Upload → the page
 * dry-runs automatically and shows the plan (what would be created vs.
 * already exists, plus any row errors) → Commit writes it.
 *
 * Structural only — no employees/approvers. Gated on `flags.hrOrgStructureImport`.
 */
import { useRef, useState, type CSSProperties } from 'react'
import { PageHead } from '../components/primitives/PageHead'
import { Card } from '../components/primitives/Card'
import { Button } from '../components/primitives/Button'
import { Icon } from '../components/primitives/Icon'
import { useToast } from '../components/primitives/Toast'
import {
  parseOrgStructureCsv,
  importOrgStructure,
  type OrgStructureRow,
  type OrgStructureImportResult,
  type LevelPlan,
} from '../api/hr-org-structure-import'

const TEMPLATE =
  'company,function,department,team,function_head,department_head\n'

const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.'
}

function LevelSummary({ label, plan }: { label: string; plan: LevelPlan }): JSX.Element {
  return (
    <div data-testid={`org-import-level-${label.toLowerCase()}`} style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
        {label}:{' '}
        <span style={{ color: 'var(--steel-blue)' }}>{plan.create.length} to create</span>
        <span style={{ color: 'var(--text-3)' }}>
          {' · '}
          {plan.existing.length} existing
          {plan.reparent.length > 0 ? ` · ${plan.reparent.length} re-parented` : ''}
        </span>
      </div>
      {plan.create.length > 0 ? (
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: 'var(--text-2)', fontSize: 13 }}>
          {plan.create.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function HrOrgStructureImportPage(): JSX.Element {
  const { notify } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<OrgStructureRow[]>([])
  const [parseErrors, setParseErrors] = useState<Array<{ line: number; message: string }>>([])
  const [preview, setPreview] = useState<OrgStructureImportResult | null>(null)
  const [committed, setCommitted] = useState<OrgStructureImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)

  function reset(): void {
    setRows([])
    setParseErrors([])
    setPreview(null)
    setCommitted(null)
    setFileName(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function onFile(file: File): Promise<void> {
    reset()
    setFileName(file.name)
    const text = await file.text()
    const parsed = parseOrgStructureCsv(text)
    setRows(parsed.rows)
    setParseErrors(parsed.errors)
    if (parsed.rows.length === 0) {
      notify('No valid rows found in the CSV.', 'error')
      return
    }
    setBusy(true)
    try {
      setPreview(await importOrgStructure(parsed.rows, true))
    } catch (err) {
      notify(errMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function commit(): Promise<void> {
    setBusy(true)
    try {
      const result = await importOrgStructure(rows, false)
      setCommitted(result)
      setPreview(null)
      notify('Org structure imported.', 'success')
    } catch (err) {
      notify(errMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  const hasServerErrors = (preview?.errors.length ?? 0) > 0
  // A head-only re-import (structure already exists, only heads to assign) is
  // still a valid commit, so heads count as "something to do".
  const nothingToCreate =
    preview != null &&
    preview.companies.create.length === 0 &&
    preview.functions.create.length === 0 &&
    preview.departments.create.length === 0 &&
    preview.teams.create.length === 0 &&
    preview.teams.reparent.length === 0 &&
    preview.heads.length === 0

  return (
    <>
      <PageHead
        eyebrow="HR"
        title="Import org structure"
        subtitle="Upload one CSV with columns company, function, department, team — one row per team. The company, functions and departments are created from the distinct values. Two optional columns, function_head and department_head, set each unit's head (matched by employee name or email); leave them blank on the first pass and fill them in once employees are loaded. Preview before committing."
      />

      <Card padding={20} style={{ marginBottom: 16 }}>
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload org-structure CSV"
          data-testid="org-import-dropzone"
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              fileRef.current?.click()
            }
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            const f = e.dataTransfer.files?.[0]
            if (f) void onFile(f)
          }}
          style={{
            border: `2px dashed ${dragOver ? 'var(--steel-blue)' : 'var(--border)'}`,
            borderRadius: 12,
            padding: '32px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? 'var(--primary-bg)' : 'var(--surface)',
            transition: 'background 120ms ease, border-color 120ms ease',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--primary-bg)',
              color: 'var(--steel-blue)',
              marginBottom: 12,
            }}
          >
            <Icon name={fileName ? 'description' : 'cloud_upload'} size={26} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>
            {fileName ? fileName : 'Drag your CSV here, or click to browse'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {busy
              ? 'Reading…'
              : fileName
                ? 'Click to choose a different file'
                : 'One row per team — columns: company, function, department, team (+ optional function_head, department_head)'}
          </div>
          <input
            ref={fileRef}
            data-testid="org-import-file"
            type="file"
            accept=".csv,text/csv"
            aria-hidden="true"
            tabIndex={-1}
            style={VISUALLY_HIDDEN}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void onFile(f)
            }}
          />
        </div>
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <button
            type="button"
            data-testid="org-import-template"
            onClick={() => {
              const blob = new Blob([TEMPLATE], { type: 'text/csv' })
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = 'lms-org-structure-template.csv'
              a.click()
              URL.revokeObjectURL(a.href)
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--steel-blue)',
              cursor: 'pointer',
              fontSize: 13,
              textDecoration: 'underline',
            }}
          >
            Download template
          </button>
        </div>
        {parseErrors.length > 0 ? (
          <div data-testid="org-import-parse-errors" style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger-fg)' }}>
              {parseErrors.length} row(s) skipped (bad/blank cells):
            </div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--text-2)' }}>
              {parseErrors.slice(0, 10).map((e) => (
                <li key={e.line}>line {e.line}: {e.message}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>

      {preview ? (
        <Card padding={20} data-testid="org-import-preview">
          <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 500 }}>
            Preview — {preview.totalRows} rows
          </h2>
          <LevelSummary label="Companies" plan={preview.companies} />
          <LevelSummary label="Functions" plan={preview.functions} />
          <LevelSummary label="Departments" plan={preview.departments} />
          <LevelSummary label="Teams" plan={preview.teams} />

          {preview.heads.length > 0 ? (
            <div data-testid="org-import-heads" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                Heads:{' '}
                <span style={{ color: 'var(--steel-blue)' }}>
                  {preview.heads.length} to assign
                </span>
              </div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: 'var(--text-2)', fontSize: 13 }}>
                {preview.heads.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {hasServerErrors ? (
            <div data-testid="org-import-errors" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger-fg)' }}>
                {preview.errors.length} error(s) — fix the CSV before committing:
              </div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--text-2)' }}>
                {preview.errors.map((e) => (
                  <li key={e.rowIndex}>row {e.rowIndex + 1}: {e.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <Button
              variant="primary"
              data-testid="org-import-commit"
              disabled={busy || hasServerErrors || nothingToCreate}
              onClick={() => void commit()}
            >
              {nothingToCreate ? 'Nothing to import' : 'Commit import'}
            </Button>
            <Button variant="secondary" onClick={reset} disabled={busy}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      {committed ? (
        <Card padding={20} data-testid="org-import-done">
          <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 500 }}>Import complete</h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)' }}>
            Created {committed.companies.create.length} company(ies),{' '}
            {committed.functions.create.length} function(s),{' '}
            {committed.departments.create.length} department(s), and{' '}
            {committed.teams.create.length} team(s)
            {committed.heads.length > 0
              ? `, and assigned ${committed.heads.length} head(s)`
              : ''}
            .
          </p>
          <div style={{ marginTop: 12 }}>
            <Button variant="secondary" onClick={reset}>
              Import another
            </Button>
          </div>
        </Card>
      ) : null}
    </>
  )
}
